/**
 * Fires an anonymous heartbeat to /api/telemetry/visitor every 15s so the admin
 * live-visitors panel can see who is browsing right now. Piggy-backs on the same
 * `session_id` we already use for cart/download authorization.
 *
 * Skips heartbeat when the current path is inside /admin so admins don't clutter
 * their own dashboard.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEARTBEAT_MS = 15000;

const ensureSessionId = () => {
  let sid = localStorage.getItem('session_id');
  if (!sid) {
    sid = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('session_id', sid);
  }
  return sid;
};

export const useVisitorHeartbeat = () => {
  const location = useLocation();
  const timerRef = useRef(null);
  const lastRefRef = useRef(document.referrer || '');

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path.startsWith('/admin')) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    const send = () => {
      const sid = ensureSessionId();
      // best-effort — never surface network errors to the user
      axios.post(`${API}/telemetry/visitor`, {
        session_id: sid,
        path,
        referrer: lastRefRef.current || null,
      }, { timeout: 8000 }).catch(() => {});
    };
    send();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(send, HEARTBEAT_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [location.pathname, location.search]);
};
